import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { StatCard, PageHeader, BrutalCard } from "../components/brutal";
import { Coins, Users, CheckCircle2, Clock, Trophy, Sparkles, ArrowRight, Instagram, Megaphone, Flame, PartyPopper, Linkedin, Share2, X as XIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { brandConfetti } from "../lib/confetti";

/** Hook: animate a number from 0 → target on mount, like a slot-machine ticker. */
function useCountUp(target, durationMs = 1200) {
  const [value, setValue] = useState(0);
  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) return;
    if (!target || target <= 0) { setValue(0); return; }
    startedRef.current = true;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / durationMs);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
      else setValue(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return value;
}

function AnimatedPointsStat({ total }) {
  const animated = useCountUp(total, 1400);
  return (
    <StatCard
      testid="stat-points"
      label="Total Points"
      value={animated.toLocaleString()}
      sub={`= €${animated.toLocaleString()} in payroll`}
      color="bg-brand-yellow"
      icon={<Coins className="w-6 h-6" strokeWidth={2.4} />}
    />
  );
}

const STAGE_COLOR = {
  "Submitted": "bg-white",
  "Shortlisted": "bg-brand-yellow",
  "Interview Scheduled": "bg-brand-blue text-white",
  "Interview Completed": "bg-brand-blue text-white",
  "Offer Made": "bg-brand-orange text-white",
  "Hired": "bg-brand-green",
  "Passed Probation": "bg-brand-green",
};

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const isEmployee = user.role === "employee";
  const [data, setData] = useState(null);
  const [spotlight, setSpotlight] = useState(null);
  const [topShout, setTopShout] = useState(null);

  useEffect(() => {
    if (isEmployee) {
      api.get("/dashboard/me").then((r) => setData(r.data));
    } else {
      setData({}); // admin: no employee stats needed
    }
    api.get("/spotlight").then((r) => setSpotlight(r.data)).catch(() => {});
    api.get("/shoutouts/top", { params: { period: "week" } })
      .then((r) => setTopShout(r.data))
      .catch(() => {});
  }, [isEmployee]);

  if (!data) return <div className="p-10 text-center">Loading…</div>;

  // Animated points ticker
  const animatedPoints = (isEmployee && data.total_points) ? data.total_points : 0;

  return (
    <div>
      <PageHeader
        title={`Hey, ${user.name.split(" ")[0]}.`}
        subtitle={
          isEmployee
            ? "Here's where your referrals stand today. Every point = €1 in your payroll."
            : "Quick pulse on what's happening across the team."
        }
        right={
          isEmployee ? (
            <Link to="/app/refer" className="btn-brutal btn-primary" data-testid="refer-now-btn">
              Refer a candidate <ArrowRight className="w-4 h-4" />
            </Link>
          ) : (
            <Link to="/app/admin" className="btn-brutal btn-primary" data-testid="go-admin-btn">
              Open admin console <ArrowRight className="w-4 h-4" />
            </Link>
          )
        }
      />

      {/* Hire celebration: visible for any hire updated in the last 7 days, dismissable per-referral. */}
      {isEmployee && (data.recent_hires || []).length > 0 && (
        <HireCelebrations hires={data.recent_hires} />
      )}

      {/* Almost-hired banner: shows when any referred candidate is in a late stage */}
      {isEmployee && (data.almost_hired || []).length > 0 && (
        <BrutalCard
          className="p-5 mb-6"
          style={{ background: "linear-gradient(135deg, #FFD600 0%, #FF5C00 100%)" }}
          data-testid="almost-hired-banner"
        >
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 bg-black border-2 border-black rounded-2xl flex items-center justify-center shadow-brutal flex-shrink-0">
              <PartyPopper className="w-6 h-6 text-brand-yellow" strokeWidth={2.6} />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest font-bold text-black/80">Almost there</div>
              <h3 className="font-display font-black text-xl sm:text-2xl leading-tight mt-1">
                {data.almost_hired.length === 1
                  ? `${data.almost_hired[0].candidate_name} is close to getting hired!`
                  : `You have ${data.almost_hired.length} referrals in the final stretch.`}
              </h3>
              <div className="mt-2 flex flex-wrap gap-2">
                {data.almost_hired.map((r) => (
                  <span key={r.referral_id} className="text-xs font-bold uppercase tracking-wider px-2 py-1 border-2 border-black rounded-full bg-white">
                    {r.candidate_name} · {r.stage}{r.job_title ? ` · ${r.job_title}` : ""}
                  </span>
                ))}
              </div>
              <Link to="/app/my-referrals" className="btn-brutal bg-black text-white border-black mt-3 inline-flex text-xs" data-testid="almost-hired-cta">
                See their progress <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </BrutalCard>
      )}

      {/* Streak chip: consecutive months with a hire */}
      {isEmployee && (data.streak_months || 0) >= 2 && (
        <div
          className="inline-flex items-center gap-2 mb-6 px-4 py-2 border-2 border-black rounded-full font-bold text-sm shadow-brutal"
          style={{ background: "linear-gradient(90deg, #FF3AC5, #FF5C00)" }}
          data-testid="streak-chip"
          title={`You've had at least one successful hire every month for the last ${data.streak_months} months.`}
        >
          <Flame className="w-4 h-4 text-white" strokeWidth={2.6} />
          <span className="text-white">{data.streak_months}-month hire streak — keep it alive!</span>
        </div>
      )}

      {/* Employee-only bento grid of personal stats */}
      {isEmployee && (
        <div className="bento-grid mb-8">
          <AnimatedPointsStat total={animatedPoints} />
          <StatCard
            testid="stat-referrals"
            label="Total Referrals"
            value={data.total_referrals}
            sub="All candidates referred"
            color="bg-white"
            icon={<Users className="w-6 h-6" strokeWidth={2.4} />}
          />
          <StatCard
            testid="stat-hires"
            label="Successful Hires"
            value={data.hired_referrals}
            sub="Candidates now employed"
            color="bg-brand-green"
            icon={<CheckCircle2 className="w-6 h-6" strokeWidth={2.4} />}
          />
          <StatCard
            testid="stat-rank"
            label="Leaderboard Rank"
            value={data.rank ? `#${data.rank}` : "—"}
            sub="Company-wide"
            color="bg-brand-orange text-white"
            icon={<Trophy className="w-6 h-6 text-white" strokeWidth={2.4} />}
          />
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Spotlight teaser */}
        {spotlight && (
          <Link to="/app/spotlight" className="lg:col-span-3" data-testid="dashboard-spotlight">
            <div className="brutal brutal-hover p-6 flex flex-col md:flex-row items-center gap-6"
                 style={{ background: "linear-gradient(135deg, #FF5C00 0%, #FF3AC5 50%, #0047FF 100%)", color: "#fff" }}>
              <div className="flex items-center gap-4 flex-1">
                <img
                  src={spotlight.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(spotlight.name)}`}
                  alt=""
                  className="w-20 h-20 rounded-2xl border-4 border-white object-cover shadow-[4px_4px_0_0_#09090B]"
                />
                <div>
                  <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold bg-white text-black px-3 py-1 rounded-full border-2 border-black">
                    <Instagram className="w-3 h-3" /> Spotlight
                  </div>
                  <div className="font-display font-black text-3xl md:text-4xl mt-2">{spotlight.name}</div>
                  <div className="text-sm opacity-90">{spotlight.headline}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-white text-black border-2 border-black rounded-2xl px-4 py-2 font-bold shadow-[4px_4px_0_0_#09090B]">
                View & share <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </Link>
        )}

        {/* Shout-out of the week */}
        {topShout && (
          <Link to="/app/shoutouts" className="lg:col-span-3" data-testid="dashboard-shoutout">
            <div
              className="brutal brutal-hover p-6 flex flex-col md:flex-row items-center gap-6"
              style={{ background: "linear-gradient(135deg, #FFD600 0%, #FF5C00 55%, #FF3AC5 100%)", color: "#09090B" }}
            >
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <img
                  src={topShout.recipient_avatar || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(topShout.recipient_name || "?")}`}
                  alt=""
                  crossOrigin="anonymous"
                  className="w-20 h-20 rounded-2xl border-4 border-black object-cover shadow-[4px_4px_0_0_#09090B] bg-white flex-shrink-0"
                />
                <div className="min-w-0">
                  <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold bg-black text-white px-3 py-1 rounded-full border-2 border-black">
                    <Megaphone className="w-3 h-3" /> Shout-out of the week
                  </div>
                  <div className="font-display font-black text-2xl md:text-3xl mt-2 truncate">{topShout.recipient_name}</div>
                  <div className="text-sm leading-snug line-clamp-2">{topShout.message}</div>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                {topShout.reactions_total > 0 && (
                  <span className="inline-flex items-center gap-1 bg-black text-white border-2 border-black rounded-full px-3 py-1.5 text-xs font-bold">
                    <Flame className="w-3.5 h-3.5" /> {topShout.reactions_total}
                  </span>
                )}
                {topShout.bonus_points > 0 && (
                  <span className="chip bg-white text-xs font-black border-2 border-black">
                    <Sparkles className="w-3 h-3" /> +{topShout.bonus_points}
                  </span>
                )}
                <div className="flex items-center gap-2 bg-white text-black border-2 border-black rounded-2xl px-4 py-2 font-bold shadow-[4px_4px_0_0_#09090B]">
                  React <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </div>
          </Link>
        )}

        {/* Recent referrals — employees only */}
        {isEmployee && (
          <BrutalCard className="p-6 lg:col-span-3">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display font-black text-2xl">Recent referrals</h2>
              <Link to="/app/my-referrals" className="text-sm font-bold underline" data-testid="view-all-referrals">View all</Link>
            </div>
            {data.recent_referrals?.length === 0 ? (
              <div className="text-neutral-600">No referrals yet. Submit your first one!</div>
            ) : (
              <div className="divide-y-2 divide-black/10">
                {data.recent_referrals?.map((r) => (
                  <div key={r.id} className="py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{r.candidate_name}</div>
                      <div className="text-xs text-neutral-600 truncate">{r.job_title}</div>
                    </div>
                    <span className={`chip ${STAGE_COLOR[r.current_stage_name] || "bg-white"}`}>
                      {r.current_stage_name}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </BrutalCard>
        )}

        {data.bonus_transactions && data.bonus_transactions.length > 0 && (
          <BrutalCard className="p-6 lg:col-span-3" color="bg-white" data-testid="dashboard-bonus-section">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-5">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 bg-brand-yellow border-2 border-black rounded-xl flex items-center justify-center shadow-brutal">
                  <Sparkles className="w-5 h-5" strokeWidth={2.5} />
                </div>
                <div>
                  <h2 className="font-display font-black text-2xl leading-none">Bonus points received</h2>
                  <p className="text-sm text-neutral-700 mt-1">Ad-hoc recognition from your admins.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="border-2 border-black rounded-xl px-4 py-2 bg-brand-green shadow-brutal">
                  <div className="text-[10px] uppercase tracking-widest font-bold">Awarded</div>
                  <div className="font-display font-black text-2xl leading-none">+{data.bonus_total_awarded || 0}</div>
                </div>
                <div className="border-2 border-black rounded-xl px-4 py-2 bg-white shadow-brutal">
                  <div className="text-[10px] uppercase tracking-widest font-bold">Net</div>
                  <div className="font-display font-black text-2xl leading-none">{data.bonus_total_net > 0 ? "+" : ""}{data.bonus_total_net || 0}</div>
                </div>
              </div>
            </div>
            <div className="divide-y-2 divide-black/10">
              {data.bonus_transactions.slice(0, 5).map((t) => (
                <div key={t.id} className="py-3 flex items-center justify-between gap-3" data-testid={`bonus-activity-${t.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{(t.reason || "").replace(/^Bonus:\s*/i, "")}</div>
                    <div className="text-xs text-neutral-600">
                      {t.awarded_by_name ? `by ${t.awarded_by_name} · ` : ""}{new Date(t.created_at).toLocaleString()}
                    </div>
                  </div>
                  <span className={`chip ${t.points > 0 ? "bg-brand-green" : "bg-red-200"}`}>
                    {t.points > 0 ? `+${t.points}` : t.points} pts
                  </span>
                </div>
              ))}
            </div>
          </BrutalCard>
        )}

        {/* Badges preview — employees only */}
        {isEmployee && (
          <BrutalCard className="p-6 lg:col-span-3" color="bg-white">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5" />
                <h2 className="font-display font-black text-2xl">Your Badges</h2>
              </div>
              <Link to="/app/badges" className="text-sm font-bold underline" data-testid="view-all-badges">All badges</Link>
            </div>
            {data.badges?.length === 0 ? (
              <div className="text-neutral-600">No badges yet — submit your first referral to earn one!</div>
            ) : (
              <div className="flex gap-4 overflow-x-auto pb-2">
                {data.badges?.map((b) => (
                  <div key={b.id} className="flex-shrink-0 w-48 border-2 border-black rounded-2xl p-4 text-center bg-brand-yellow shadow-brutal">
                    {b.icon_url && (
                      <img src={b.icon_url} alt={b.name} className="w-28 h-28 mx-auto object-contain" />
                    )}
                    <div className="font-display font-black mt-2">{b.name}</div>
                    <div className="text-xs text-neutral-700 mt-1">{b.description}</div>
                  </div>
                ))}
              </div>
            )}
          </BrutalCard>
        )}
      </div>
    </div>
  );
}

const HIRE_DISMISS_KEY = "rp_hire_dismissed_v1";

function loadDismissed() {
  try {
    const raw = localStorage.getItem(HIRE_DISMISS_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch { return new Set(); }
}

function saveDismissed(set) {
  try {
    localStorage.setItem(HIRE_DISMISS_KEY, JSON.stringify(Array.from(set)));
  } catch { /* ignore */ }
}

function HireCelebrations({ hires }) {
  const [dismissed, setDismissed] = useState(loadDismissed);
  const fired = useRef(false);

  const visible = hires.filter((h) => !dismissed.has(h.referral_id));

  useEffect(() => {
    if (visible.length > 0 && !fired.current) {
      fired.current = true;
      brandConfetti({ intensity: "big" });
    }
  }, [visible.length]);

  if (visible.length === 0) return null;

  const dismiss = (id) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(id);
      saveDismissed(next);
      return next;
    });
  };

  return (
    <div className="space-y-3 mb-6" data-testid="hire-celebrations">
      {visible.map((h) => (
        <HireCard key={h.referral_id} hire={h} onDismiss={() => dismiss(h.referral_id)} />
      ))}
    </div>
  );
}

function HireCard({ hire, onDismiss }) {
  const linkedinShare = () => {
    const text = `🎉 Just helped land another great hire — ${hire.candidate_name} for ${hire.job_title || "a new role"}! Proud of my team. #ReferPro`;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(origin)}&summary=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener");
  };

  return (
    <BrutalCard
      className="p-5 relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #00E676 0%, #0047FF 70%, #FF3AC5 100%)" }}
      data-testid={`hire-celebration-${hire.referral_id}`}
    >
      <button
        onClick={onDismiss}
        className="absolute top-2 right-2 w-8 h-8 border-2 border-black bg-white rounded-xl flex items-center justify-center"
        data-testid={`hire-dismiss-${hire.referral_id}`}
        aria-label="Dismiss"
      >
        <XIcon className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3 pr-10">
        <div className="w-12 h-12 bg-black border-2 border-black rounded-2xl flex items-center justify-center shadow-brutal flex-shrink-0">
          <PartyPopper className="w-6 h-6 text-brand-yellow" strokeWidth={2.6} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-bold text-white/90">Hired!</div>
          <h3 className="font-display font-black text-2xl leading-tight text-white" data-testid={`hire-title-${hire.referral_id}`}>
            {hire.candidate_name} just got hired{hire.job_title ? ` as ${hire.job_title}` : ""}!
          </h3>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-white/95 text-sm">
            <span className="inline-flex items-center gap-1 chip bg-white text-black text-xs">
              <Coins className="w-3 h-3" />
              <strong>+{(hire.points_earned || 0).toLocaleString()}</strong> from this referral
            </span>
            <span className="text-xs">€{(hire.points_earned || 0).toLocaleString()} added to your payroll.</span>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={linkedinShare}
              className="btn-brutal text-xs bg-[#0A66C2] text-white border-black"
              data-testid={`hire-share-linkedin-${hire.referral_id}`}
            >
              <Linkedin className="w-3.5 h-3.5" /> Brag on LinkedIn
            </button>
            <Link
              to="/app/my-referrals"
              className="btn-brutal btn-white text-xs"
              data-testid={`hire-view-${hire.referral_id}`}
            >
              View all referrals <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </BrutalCard>
  );
}
